import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';

import env from 'Env';
import { addToCartFromPixAndMix, removeFromCart } from 'Redux/ducks/cart';
import AgeRatings from 'Components/Product/Age/AgeRatings';
import BundleVrSupport from 'Components/Bundle/BundleCarousels/BundleVrSupport';
import DangerousHTML from 'Components/Utility/DangerousHTML';
import Drm from 'Components/Utility/Drm';
import FormatProductName from 'Helpers/Format/FormatProductName';
import ProductStatusMessage from 'Components/Bundle/BundleCarousels/ProductStatusMessage';
import SteamDeckInformation from 'Components/Utility/SteamDeck/SteamDeckInformation';
import SystemRequirements from 'Components/Product/SystemRequirements/SystemRequirements';
import RegionLocked from 'Components/Product/ProductCommerce/RegionLocked';
import BundleCarouselReviewScore from 'Components/Bundle/BundleCarousels/BundleCarouselReviewScore';
import MessageBox from 'Components/Utility/MessageBox';
import BundleCarouselUserReviewScore from 'Components/Bundle/BundleCarousels/BundleCarouselUserReviewScore';
import useViewport from 'Hooks/useViewport';
import { getStatusMessage } from 'Components/Bundle/BundleCarousels/BundleCarouselHelper';

const messages = defineMessages({
  keys_pending_title: {
    id: 'gamedetails.keys-pending.title',
    defaultMessage: 'Keys Pending',
  },
  keys_pending_message: {
    id: 'gamedetails.keys-pending.message',
    defaultMessage: 'You will receive your key for this title as soon as they are available.',
  },
});

const GameDetails = ({
  slugsToBlur,
  product,
  lightThemeOverride,
}) => {
  const dispatch = useDispatch();
  const { isBelowLg } = useViewport();
  const cartSlugs = useSelector(state => state.cart.allSlugs);
  const lightTheme = useSelector((state) => {
    const siteTheme = state?.siteTheme;
    return lightThemeOverride || siteTheme === 'light';
  });
  const { formatMessage } = useIntl();

  if (!product) return false;

  const {
    age,
    drm,
    gameNames,
    lang: languages,
    name,
    steam,
    platform_specs: requirements,
    platforms: systems,
    display_type: displayType,
    slug,
    _id: id,
    type,
    vr_support: vrSupport,
    vr_requirements: vrRequirements,
    categories,
    metaCritic,
    openCritic,
    soldOut,
    regions_excluded: excludedRegions,
    regions_included: includedRegions,
    ztormId,
    preorder_delivery: preorderDelivery,
    userReviewSummary,
    hide_dlc_notification: hideDLC,
    desc,
    descs,
    ubisoft_ska: ubisoftSka,
    origin_ska: originSka,
    epic_ska: epicSka,
    features,
  } = product;

  const steamId = steam?.id;
  const steamSub = steam?.sub || false;
  const validDrm = drm ? Object.keys(drm).filter(d => drm[d]) : '';
  const validPlatforms = systems ? Object.keys(systems).filter(p => systems[p]) : {};
  const gamedescription = descs ? descs[window.locale] : null || desc;

  const statusMessage = getStatusMessage(
    features,
    type,
    ubisoftSka,
    originSka,
    epicSka,
    validDrm,
    slug,
  );

  let steamUrl = `http://store.steampowered.com/app/${steamId}`;
  if (steamSub) {
    steamUrl = `http://store.steampowered.com/sub/${steamId}`;
  }

  let englishLanguageOnly = false;
  if (!isEmpty(languages) && includes(languages, 'English') && languages.length === 1 && displayType === 'elearning') {
    englishLanguageOnly = true;
  }

  const isPickAndMix = window.location.href.split('/')[4] === 'pick-and-mix';
  const inBasket = cartSlugs.includes(slug);

  const showSystemRequirements = !isEmpty(requirements);

  let showVRSection = false;
  if (
    (!isEmpty(vrSupport) && (
      vrSupport.vr_required ||
      vrSupport.htc_vive ||
      vrSupport.oculus_rift ||
      vrSupport.valve_index ||
      vrSupport.windows_mixed_reality ||
      vrSupport.tracked_motion_controllers ||
      vrSupport.seated ||
      vrSupport.standing ||
      vrSupport.room_scale ||
      vrSupport.gamepad ||
      vrSupport.keyboard_mouse
    ))) {
    showVRSection = true;
  }

  const handleRemove = () => {
    dispatch(removeFromCart(id));
  };

  const handleAdd = () => {
    dispatch(addToCartFromPixAndMix(id, slug));
  };

  const scrollTo = (value) => {
    setTimeout(() => {
      if (document.getElementById(value)) {
        document.getElementById(value).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };


  return (
    <div
      className={classNames('content-container', {
        'blur-content': slugsToBlur.includes(slug),
      })}
      id="carousel-content"
    >
      <div className="product-details">
        <article>
          {preorderDelivery && (
            <MessageBox
              type="info"
              title={formatMessage(messages.keys_pending_title)}
              message={formatMessage(messages.keys_pending_message)}
            />
          )}
          <div className="product-name-container">
            <h3 className="product-name">{FormatProductName(name, type)}</h3>
            {isPickAndMix && !soldOut && (
              <Button
                color="primary"
                className={`pnm-add-btn ${inBasket ? 'btn-selected' : ''} text-uppercase`}
                onClick={inBasket ? handleRemove : handleAdd}
              >
                <div className="btn-text-container d-block">
                  {inBasket ? (
                    <FontAwesomeIcon icon={faTrashCan} className="mr-1" />
                  ) : (
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  )}
                  <b>
                    {inBasket ? (
                      <FormattedMessage
                        id="bundle-game-details.select.remove"
                        defaultMessage="Remove from bundle"
                      />
                    ) : (
                      <FormattedMessage
                        id="bundle-game-details.select.add"
                        defaultMessage="Add to bundle"
                      />
                    )}
                  </b>
                </div>
              </Button>
            )}
          </div>

          <div className="secondary-container">
            <div className="icons-container">
              {!isEmpty(validDrm[0]) && (
                <Drm
                  drmType={validDrm[0]}
                  colorOverride={lightTheme ? 'dark' : ''}
                />
              )}
              <div className="platform-container">
                {includes(validPlatforms, 'windows') && (
                  <span>
                    <FontAwesomeIcon icon={fabFaWindows} className="mr-2" />
                  </span>
                )}
                {includes(validPlatforms, 'mac') && (
                  <span>
                    <FontAwesomeIcon icon={fabFaApple} className="mr-2" />
                  </span>
                )}
                {includes(validPlatforms, 'linux') && (
                  <span>
                    <FontAwesomeIcon icon={fabFaLinux} className="mr-2" />
                  </span>
                )}
              </div>
            </div>

            <div className="links-container">
              {englishLanguageOnly && (
                <div className="badge badge-primary">
                  <FormattedMessage
                    id="bundle-game-details.english-language"
                    defaultMessage="English Language Only"
                  />
                </div>
              )}
              {displayType !== 'elearning' && showSystemRequirements && (
                <button
                  className="anchor-button"
                  onClick={() => scrollTo('sysreq')}
                >
                  <FormattedMessage
                    id="bundle-game-details.system-requirements"
                    defaultMessage="System Requirements"
                  />
                </button>
              )}

              {displayType !== 'elearning' && showVRSection && (
                <button
                  className="anchor-button"
                  onClick={() => scrollTo('vrsupport')}
                >
                  <FormattedMessage
                    id="bundle-game-details.vrsupport"
                    defaultMessage="VR Support"
                  />
                </button>
              )}
              {steam &&
                steam.id &&
                displayType !== 'elearning' &&
                validDrm[0] !== 'uplay' && (
                  <a
                    href={steamUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-none d-md-inline"
                  >
                    <FormattedMessage
                      id="bundle-game-details.steam-view"
                      defaultMessage="View on Steam"
                    />
                  </a>)
              }
            </div>
          </div>

          <div className="secondary-container">
            {(steam?.deck_support === 'verified' || steam?.deck_support === 'playable') && (
              <SteamDeckInformation
                longTitle
                type={steam?.deck_support}
                details={steam?.deck_details}
                product={name}
              />
            )}

            {isPickAndMix && (
              <div className="d-flex align-center">
                <BundleCarouselUserReviewScore
                  userReviewSummary={userReviewSummary}
                />

                {!isBelowLg &&
                  <BundleCarouselReviewScore
                    metaCritic={metaCritic}
                    openCritic={openCritic}
                  />
                }

              </div>
            )}
          </div>

          {!isEmpty(categories) && (
            <div className="tag-column-container">
              {categories.slice(0, 8).map(tag => (
                <React.Fragment key={tag.slug || tag}>
                  {typeof tag === 'string' ? (
                    <div className="tag-column" key={tag}>
                      <span className="tag">{tag}</span>
                    </div>
                  ) : (
                    <div className="tag-column" key={tag.slug}>
                      {tag.link && env.linkBundleCarouselTags ? (
                        <Link to={`/categories/${tag.slug}`} className="tag">
                          {tag.name}
                        </Link>
                      ) : (
                        <span className="tag">{tag.name}</span>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          <RegionLocked
            included={includedRegions}
            excluded={excludedRegions}
            ztormId={ztormId}
          />

          {!hideDLC && (
            <ProductStatusMessage statusMessage={statusMessage} type={type} />
          )}

          {!isEmpty(gameNames) && (
            <div>
              <h4>
                <FormattedMessage
                  id="bundle-game-details.bundle.includes"
                  defaultMessage="This bundle includes:"
                />
              </h4>
              <ul>
                {gameNames.map(gameName => (
                  <li>{gameName}</li>
                ))}
              </ul>
            </div>
          )}

          <DangerousHTML tag="p" html={gamedescription} />
        </article>

        <AgeRatings showTitle ratings={age} slug={slug} />

        {displayType !== 'elearning' && showSystemRequirements && (
          <React.Fragment>
            <hr id="sysreq" className="sysreq" />
            <article>
              <h4>
                <FormattedMessage
                  id="bundle-game-details.sys-rec"
                  defaultMessage="System Requirements"
                />
              </h4>
              <SystemRequirements
                systems={systems}
                requirements={requirements}
              />
            </article>
          </React.Fragment>
        )}

        {showVRSection && (
          <React.Fragment>
            <hr id="vrsupport" />
            <BundleVrSupport vr={vrSupport} requirements={vrRequirements} />
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

GameDetails.propTypes = {
  product: PropTypes.object,
  slugsToBlur: PropTypes.array,
  lightThemeOverride: PropTypes.bool,
};

GameDetails.defaultProps = {
  product: null,
  slugsToBlur: [],
  lightThemeOverride: false,
};

export default GameDetails;
